/**
 * SchedulesPage — Manages inspection schedules linking templates to machines.
 *
 * Displays a FilterableDataTable with columns: Template, Machine, Frequency,
 * Next Run, Active, Actions. Supports create/edit via DrawerPanel and
 * toggle active/inactive with a single click.
 *
 * @see Requirements 7.1, 7.2, 7.3, 7.4, 7.5, 7.6
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GridColDef } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import EditIcon from '@mui/icons-material/Edit';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import DeleteIcon from '@mui/icons-material/Delete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';

import {
  FilterableDataTable,
  DrawerPanel,
  ConfirmDialog,
  PageContainer,
} from '../../components/shared';
import type { FilterConfig, RowAction, DrawerAction } from '../../components/shared';
import {
  InspectionSchedulesApi,
  MachineControllerApi,
  ChecklistTemplateControllerApi,
} from '../../api-generated/apis';
import type {
  Machine,
  ChecklistTemplate,
  CreateScheduleRequest,
} from '../../api-generated/models';
import { CreateScheduleRequestFrequencyEnum } from '../../api-generated/models';
import { apiConfig } from '../../api-generated/apiConfig';

const schedulesApi = new InspectionSchedulesApi(apiConfig);
const machineApi = new MachineControllerApi(apiConfig);
const templateApi = new ChecklistTemplateControllerApi(apiConfig);

// ─── Types ───────────────────────────────────────────────────────────────────

type ScheduleFrequency = 'DAILY' | 'WEEKLY' | 'PER_SHIFT' | 'CUSTOM';

interface Schedule {
  id: string;
  templateId: string;
  templateName: string;
  machineId: string;
  machineName: string;
  frequency: ScheduleFrequency;
  cronExpression: string;
  shiftsPerDay: string;
  shiftDurationHours: string;
  firstShiftStart: string;
  nextRun: string;
  startDate: string;
  endDate: string;
  active: boolean;
}

interface ScheduleFormData {
  templateId: string;
  machineId: string;
  frequency: ScheduleFrequency | '';
  cronExpression: string;
  shiftsPerDay: string;
  shiftDurationHours: string;
  firstShiftStart: string;
  startDate: string;
  endDate: string;
  active: boolean;
}

interface ScheduleFormErrors {
  cronExpression?: string;
  shiftsPerDay?: string;
  shiftDurationHours?: string;
  firstShiftStart?: string;
}

interface TemplateOption {
  id: string;
  name: string;
}

interface MachineOption {
  id: string;
  name: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const FREQUENCY_OPTIONS: { value: ScheduleFrequency; label: string }[] = [
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'PER_SHIFT', label: 'Per Shift' },
  { value: 'CUSTOM', label: 'Custom (Cron)' },
];

const INITIAL_FORM: ScheduleFormData = {
  templateId: '',
  machineId: '',
  frequency: '',
  cronExpression: '',
  shiftsPerDay: '',
  shiftDurationHours: '',
  firstShiftStart: '',
  startDate: new Date().toISOString().split('T')[0],
  endDate: '',
  active: true,
};

/**
 * Formats a schedule timestamp for display.
 *
 * The API returns instant fields (nextGenerationAt, lastGeneratedAt) as an
 * epoch value in SECONDS with a fractional part (e.g. 1789511454.278613),
 * not milliseconds and not ISO. A numeric value below ~1e12 is treated as
 * seconds and scaled to milliseconds; ISO strings are parsed as-is.
 * Returns null for empty/invalid values so the caller can show a placeholder.
 */
function formatTimestamp(value: unknown): string | null {
  if (value == null || value === '') return null;

  let date: Date;
  if (typeof value === 'number') {
    // Epoch seconds → ms when the magnitude is too small to be ms.
    const ms = value < 1e12 ? value * 1000 : value;
    date = new Date(ms);
  } else if (typeof value === 'string' && /^\d+(\.\d+)?$/.test(value)) {
    // Numeric string epoch (seconds).
    const num = Number(value);
    date = new Date(num < 1e12 ? num * 1000 : num);
  } else {
    date = new Date(value as string);
  }

  return isNaN(date.getTime()) ? null : date.toLocaleString();
}

// ─── Component ───────────────────────────────────────────────────────────────

const SchedulesPage: React.FC = () => {
  const queryClient = useQueryClient();

  // ─── Pagination state ──────────────────────────────────────────────────
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // ─── Drawer state ──────────────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);
  const [formData, setFormData] = useState<ScheduleFormData>(INITIAL_FORM);
  const [errors, setErrors] = useState<ScheduleFormErrors>({});

  // ─── Delete confirmation state ─────────────────────────────────────────
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState<Schedule | null>(null);

  // ─── Queries ───────────────────────────────────────────────────────────

  const schedulesQuery = useQuery<any[]>({
    queryKey: ['schedules', page, pageSize],
    // NOTE: The generated getAllSchedules() types the response as a single
    // InspectionScheduleResponse, but the API actually returns an array. The
    // typed deserializer collapses the array into one empty object, so we read
    // the raw HTTP response and parse the JSON array directly.
    queryFn: async () => {
      const response = await schedulesApi.getAllSchedulesRaw({});
      const data = await response.raw.json();
      return Array.isArray(data) ? data : data?.content ?? [];
    },
  });

  const templatesQuery = useQuery<ChecklistTemplate[]>({
    queryKey: ['templates-options'],
    queryFn: () => templateApi.getAllTemplates({}),
    staleTime: 60_000,
  });

  const machinesQuery = useQuery<Machine[]>({
    queryKey: ['machines-options'],
    queryFn: () => machineApi.getAllMachines(),
    staleTime: 60_000,
  });

  // Transform template data to options
  const templateOptions: TemplateOption[] = useMemo(
    () => (templatesQuery.data ?? []).map((t: any) => ({ id: t.id ?? '', name: t.name ?? '' })),
    [templatesQuery.data],
  );

  // Transform machine data to options
  const machineOptions: MachineOption[] = useMemo(
    () => (machinesQuery.data ?? []).map((m: any) => ({ id: m.id ?? '', name: m.name ?? '' })),
    [machinesQuery.data],
  );

  // Map raw schedule response to Schedule rows
  const scheduleRows: Schedule[] = useMemo(() => {
    const raw = schedulesQuery.data;
    // Handle both array and paginated response shapes
    const items: any[] = Array.isArray(raw) ? raw : raw?.content ?? [];
    return items.map((item: any) => ({
      id: item.id ?? '',
      templateId: item.templateId ?? '',
      templateName: item.templateName ?? templateOptions.find((t) => t.id === item.templateId)?.name ?? '',
      machineId: item.machineId ?? '',
      machineName: item.machineName ?? machineOptions.find((m) => m.id === item.machineId)?.name ?? '',
      frequency: item.frequency ?? 'DAILY',
      cronExpression: item.cronExpression ?? '',
      shiftsPerDay: item.shiftPattern?.shiftsPerDay?.toString() ?? '',
      shiftDurationHours: item.shiftPattern?.shiftDurationHours?.toString() ?? '',
      firstShiftStart: item.shiftPattern?.firstShiftStart ?? '',
      nextRun: item.nextGenerationAt ?? item.nextRun ?? '',
      startDate: item.startDate ?? '',
      endDate: item.endDate ?? '',
      active: item.active ?? false,
    }));
  }, [schedulesQuery.data, templateOptions, machineOptions]);

  const totalCount = scheduleRows.length;

  // ─── Mutations ─────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (data: CreateScheduleRequest) =>
      schedulesApi.createSchedule({ createScheduleRequest: data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      handleCloseDrawer();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateScheduleRequest }) =>
      schedulesApi.updateSchedule({ id, createScheduleRequest: data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      handleCloseDrawer();
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      schedulesApi.setScheduleActive({ id, active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => schedulesApi.deleteSchedule({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      setDeleteDialogOpen(false);
      setScheduleToDelete(null);
    },
  });

  // ─── Drawer handlers ───────────────────────────────────────────────────

  const handleOpenCreate = useCallback(() => {
    setFormData(INITIAL_FORM);
    setErrors({});
    setEditingSchedule(null);
    setDrawerMode('create');
    setDrawerOpen(true);
  }, []);

  const handleOpenEdit = useCallback((schedule: Schedule) => {
    setFormData({
      templateId: schedule.templateId,
      machineId: schedule.machineId,
      frequency: schedule.frequency,
      cronExpression: schedule.cronExpression || '',
      shiftsPerDay: schedule.shiftsPerDay || '',
      shiftDurationHours: schedule.shiftDurationHours || '',
      firstShiftStart: schedule.firstShiftStart || '',
      startDate: schedule.startDate || new Date().toISOString().split('T')[0],
      endDate: schedule.endDate || '',
      active: schedule.active,
    });
    setErrors({});
    setEditingSchedule(schedule);
    setDrawerMode('edit');
    setDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setEditingSchedule(null);
    setFormData(INITIAL_FORM);
    setErrors({});
  }, []);

  const handleSubmit = useCallback(() => {
    if (!formData.templateId || !formData.machineId || !formData.frequency || !formData.startDate) return;

    // Frequency-specific validation, mirroring the backend
    // (InspectionScheduleService.validateScheduleRequest).
    const nextErrors: ScheduleFormErrors = {};
    if (formData.frequency === 'CUSTOM' && !formData.cronExpression.trim()) {
      nextErrors.cronExpression = 'Cron expression is required for Custom frequency';
    }
    if (formData.frequency === 'PER_SHIFT') {
      if (!formData.shiftsPerDay || Number(formData.shiftsPerDay) <= 0) {
        nextErrors.shiftsPerDay = 'Shifts per day must be a positive number';
      }
      if (!formData.shiftDurationHours || Number(formData.shiftDurationHours) <= 0) {
        nextErrors.shiftDurationHours = 'Shift duration must be a positive number';
      }
      if (!formData.firstShiftStart) {
        nextErrors.firstShiftStart = 'First shift start time is required';
      }
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    const payload: CreateScheduleRequest = {
      templateId: String(formData.templateId),
      machineId: String(formData.machineId),
      frequency: formData.frequency as CreateScheduleRequestFrequencyEnum,
      startDate: formData.startDate,
    };

    if (formData.endDate) {
      payload.endDate = formData.endDate;
    }
    if (formData.frequency === 'CUSTOM') {
      payload.cronExpression = formData.cronExpression.trim();
    }
    if (formData.frequency === 'PER_SHIFT') {
      payload.shiftPattern = {
        shiftsPerDay: Number(formData.shiftsPerDay),
        shiftDurationHours: Number(formData.shiftDurationHours),
        firstShiftStart: formData.firstShiftStart,
      };
    }

    if (drawerMode === 'edit' && editingSchedule) {
      updateMutation.mutate({ id: String(editingSchedule.id), data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }, [formData, drawerMode, editingSchedule, updateMutation, createMutation]);

  // ─── Toggle active handler ─────────────────────────────────────────────

  const handleToggleActive = useCallback(
    (schedule: Schedule) => {
      toggleMutation.mutate({ id: String(schedule.id), active: !schedule.active });
    },
    [toggleMutation],
  );

  // ─── Delete handlers ───────────────────────────────────────────────────

  const handleRequestDelete = useCallback((schedule: Schedule) => {
    setScheduleToDelete(schedule);
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (scheduleToDelete) {
      deleteMutation.mutate(String(scheduleToDelete.id));
    }
  }, [scheduleToDelete, deleteMutation]);

  // ─── Table configuration ───────────────────────────────────────────────

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'templateName', headerName: 'Template', flex: 1, minWidth: 150 },
      { field: 'machineName', headerName: 'Machine', flex: 1, minWidth: 150 },
      {
        field: 'frequency',
        headerName: 'Frequency',
        width: 120,
        renderCell: (params) => {
          const opt = FREQUENCY_OPTIONS.find((o) => o.value === params.value);
          return opt?.label ?? params.value;
        },
      },
      {
        field: 'nextRun',
        headerName: 'Next Run',
        width: 180,
        renderCell: (params) => {
          const formatted = formatTimestamp(params.value);
          return formatted ?? '—';
        },
      },
      {
        field: 'active',
        headerName: 'Active',
        width: 100,
        renderCell: (params) => (
          <Chip
            label={params.value ? 'Active' : 'Inactive'}
            color={params.value ? 'success' : 'default'}
            size="small"
            variant="outlined"
          />
        ),
      },
    ],
    [],
  );

  const filters: FilterConfig[] = useMemo(() => {
    const machineFilterOptions = machineOptions.map((m) => ({
      value: String(m.id),
      label: m.name,
    }));

    return [
      {
        field: 'machine',
        label: 'Machine',
        type: 'select' as const,
        options: machineFilterOptions,
      },
      {
        field: 'frequency',
        label: 'Frequency',
        type: 'select' as const,
        options: FREQUENCY_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
      },
      {
        field: 'active',
        label: 'Active',
        type: 'select' as const,
        options: [
          { value: 'true', label: 'Active' },
          { value: 'false', label: 'Inactive' },
        ],
      },
    ];
  }, [machineOptions]);

  const rowActions: RowAction<Schedule>[] = useMemo(
    () => [
      {
        label: 'Edit',
        icon: EditIcon,
        onClick: handleOpenEdit,
      },
      {
        label: 'Toggle Active',
        icon: PowerSettingsNewIcon,
        onClick: handleToggleActive,
      },
      {
        label: 'Delete',
        icon: DeleteIcon,
        onClick: handleRequestDelete,
      },
    ],
    [handleOpenEdit, handleToggleActive, handleRequestDelete],
  );

  // ─── Drawer actions ────────────────────────────────────────────────────

  const drawerActions: DrawerAction[] = useMemo(
    () => [
      {
        label: 'Cancel',
        variant: 'outlined' as const,
        onClick: handleCloseDrawer,
        testId: 'schedule-form-cancel',
      },
      {
        label: drawerMode === 'edit' ? 'Update' : 'Create',
        variant: 'contained' as const,
        onClick: handleSubmit,
        disabled:
          !formData.templateId || !formData.machineId || !formData.frequency || !formData.startDate ||
          createMutation.isPending || updateMutation.isPending,
        testId: 'schedule-form-save',
      },
    ],
    [handleCloseDrawer, handleSubmit, drawerMode, formData, createMutation.isPending, updateMutation.isPending],
  );

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <PageContainer title="Schedules" subtitle="Manage inspection schedules">
      <FilterableDataTable<Schedule>
        rows={scheduleRows}
        columns={columns}
        loading={schedulesQuery.isLoading}
        totalCount={totalCount}
        searchPlaceholder="Search schedules…"
        filters={filters}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        actions={rowActions}
        primaryAction={{ label: 'New Schedule', onClick: handleOpenCreate }}
        testIdPrefix="schedules"
      />

      {/* Create/Edit Drawer */}
      <DrawerPanel
        open={drawerOpen}
        onClose={handleCloseDrawer}
        title={drawerMode === 'edit' ? 'Edit Schedule' : 'New Schedule'}
        width="narrow"
        actions={drawerActions}
        loading={createMutation.isPending || updateMutation.isPending}
        testId="schedule-form"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Template Picker */}
          <TextField
            select
            label="Template"
            value={formData.templateId}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, templateId: e.target.value }))
            }
            fullWidth
            required
            disabled={templatesQuery.isLoading}
            SelectProps={{ SelectDisplayProps: { 'data-testid': 'schedule-form-template' } as any }}
          >
            {templateOptions.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </TextField>

          {/* Machine Picker */}
          <TextField
            select
            label="Machine"
            value={formData.machineId}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, machineId: e.target.value }))
            }
            fullWidth
            required
            disabled={machinesQuery.isLoading}
            SelectProps={{ SelectDisplayProps: { 'data-testid': 'schedule-form-machine' } as any }}
          >
            {machineOptions.map((m) => (
              <MenuItem key={m.id} value={m.id}>
                {m.name}
              </MenuItem>
            ))}
          </TextField>

          {/* Frequency Selector */}
          <TextField
            select
            label="Frequency"
            value={formData.frequency}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                frequency: e.target.value as ScheduleFrequency,
              }))
            }
            fullWidth
            required
            SelectProps={{ SelectDisplayProps: { 'data-testid': 'schedule-form-frequency' } as any }}
          >
            {FREQUENCY_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          {/* CUSTOM: Cron Expression */}
          {formData.frequency === 'CUSTOM' && (
            <TextField
              label="Cron Expression"
              value={formData.cronExpression}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, cronExpression: e.target.value }))
              }
              error={!!errors.cronExpression}
              helperText={errors.cronExpression || 'e.g., 0 0 8 * * ? (every day at 8 AM)'}
              fullWidth
              required
              placeholder="0 0 8 * * ?"
            />
          )}

          {/* PER_SHIFT: Shift Pattern */}
          {formData.frequency === 'PER_SHIFT' && (
            <>
              <TextField
                label="Shifts Per Day"
                type="number"
                value={formData.shiftsPerDay}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, shiftsPerDay: e.target.value }))
                }
                error={!!errors.shiftsPerDay}
                helperText={errors.shiftsPerDay}
                fullWidth
                required
                inputProps={{ min: 1 }}
              />
              <TextField
                label="Shift Duration (hours)"
                type="number"
                value={formData.shiftDurationHours}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, shiftDurationHours: e.target.value }))
                }
                error={!!errors.shiftDurationHours}
                helperText={errors.shiftDurationHours}
                fullWidth
                required
                inputProps={{ min: 1, max: 24 }}
              />
              <TextField
                label="First Shift Start"
                type="time"
                value={formData.firstShiftStart}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, firstShiftStart: e.target.value }))
                }
                error={!!errors.firstShiftStart}
                helperText={errors.firstShiftStart || 'HH:mm format'}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}

          {/* Start Date */}
          <DatePicker
            label="Start Date"
            value={formData.startDate ? dayjs(formData.startDate) : null}
            onChange={(value: Dayjs | null) =>
              setFormData((prev) => ({
                ...prev,
                startDate: value && value.isValid() ? value.format('YYYY-MM-DD') : '',
              }))
            }
            format="YYYY-MM-DD"
            disablePast
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
              },
            }}
          />

          {/* End Date (optional) */}
          <DatePicker
            label="End Date (optional)"
            value={formData.endDate ? dayjs(formData.endDate) : null}
            onChange={(value: Dayjs | null) =>
              setFormData((prev) => ({
                ...prev,
                endDate: value && value.isValid() ? value.format('YYYY-MM-DD') : '',
              }))
            }
            format="YYYY-MM-DD"
            slotProps={{
              textField: {
                fullWidth: true,
              },
            }}
          />

          {/* Active Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={formData.active}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, active: e.target.checked }))
                }
              />
            }
            label="Active"
          />        </Box>
      </DrawerPanel>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setScheduleToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Schedule"
        message={
          scheduleToDelete
            ? `Are you sure you want to delete the schedule for "${scheduleToDelete.templateName}" on "${scheduleToDelete.machineName}"? This action cannot be undone.`
            : 'Are you sure you want to delete this schedule?'
        }
        confirmLabel="Delete"
        confirmColor="error"
      />
    </PageContainer>
  );
};

export default SchedulesPage;
